import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FlatList,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  TouchableWithoutFeedback,
} from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { View, Image } from "@lookiero/aurora";
import { ButtonIcon, LinearGradient, useScreenSize } from "@lookiero/sty-psp-ui";
import { AnchorProduct } from "../../../../../domain/projections/AnchorProduct";
import { cdnImageUrl } from "../../../shared/util/cdnImageUrl";
import { style as anchorGarmentSelectorStyle } from "./AnchorGarmentSelector.style";

interface AnchorGarmentSelectorProps {
  readonly productVariants: AnchorProduct[];
  readonly selectedAnchorProductVariantId: string;
  readonly onPress: (productVariantId: string) => void;
}

const AnchorGarmentSelector: FC<AnchorGarmentSelectorProps> = ({
  productVariants,
  selectedAnchorProductVariantId,
  onPress,
}) => {
  const screenSize = useScreenSize();
  const isDesktop = screenSize === "L";
  const flatListRef = useRef<FlatList>(null);
  const style = useMemo(() => anchorGarmentSelectorStyle(), []);

  useEffect(() => {
    if (selectedAnchorProductVariantId && flatListRef.current) {
      const index = productVariants.findIndex((item) => item.id === selectedAnchorProductVariantId);
      flatListRef.current.scrollToIndex({ index, animated: true });
    }
  }, [selectedAnchorProductVariantId, productVariants]);

  const [scrollOffset, setScrollOffset] = useState(0);
  const [leftArrowVisible, setLeftArrowVisible] = useState(false);
  const [rightArrowVisible, setRightArrowVisible] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const handleOnContainerLayout = useCallback(
    ({
      nativeEvent: {
        layout: { width },
      },
    }: LayoutChangeEvent) => setContainerWidth(width),
    [],
  );

  const handleOnContentSizeChange = useCallback((width: number) => setContentWidth(width), []);

  const handleOnScroll = useCallback(
    ({
      nativeEvent: {
        contentOffset: { x },
      },
    }: NativeSyntheticEvent<NativeScrollEvent>) => setScrollOffset(x),
    [],
  );

  const handleOnLeftArrowPress = useCallback(
    () =>
      flatListRef.current?.scrollToOffset({
        offset: scrollOffset - (containerWidth - style.itemWidth * 2),
        animated: true,
      }),
    [containerWidth, scrollOffset, style.itemWidth],
  );

  const handleOnRightArrowPress = useCallback(
    () =>
      flatListRef.current?.scrollToOffset({
        offset: scrollOffset + (containerWidth - style.itemWidth * 2),
        animated: true,
      }),
    [containerWidth, scrollOffset, style.itemWidth],
  );

  useEffect(() => {
    if (!isDesktop || !containerWidth || !contentWidth) {
      setLeftArrowVisible(false);
      setRightArrowVisible(false);

      return;
    }

    setLeftArrowVisible(scrollOffset > 0);
    setRightArrowVisible(scrollOffset + containerWidth < contentWidth);
  }, [scrollOffset, containerWidth, contentWidth, isDesktop]);

  const leftArrowAnimation = useSharedValue(leftArrowVisible ? 1 : 0);
  leftArrowAnimation.value = leftArrowVisible ? 1 : 0;
  const leftArrowAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: withTiming(leftArrowAnimation.value),
      transform: [{ translateX: withTiming((1 - leftArrowAnimation.value) * -style.arrowContainerOuterPadding) }],
    }),
    [leftArrowAnimation.value, style.arrowContainerOuterPadding],
  );

  const rightArrowAnimation = useSharedValue(rightArrowVisible ? 1 : 0);
  rightArrowAnimation.value = rightArrowVisible ? 1 : 0;
  const rightArrowAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: withTiming(rightArrowAnimation.value),
      transform: [{ translateX: withTiming((1 - rightArrowAnimation.value) * style.arrowContainerOuterPadding) }],
    }),
    [rightArrowAnimation.value, style.arrowContainerOuterPadding],
  );

  return (
    <View testID="anchor-garment-selector" onLayout={handleOnContainerLayout}>
      <Animated.View
        accessibilityState={{ disabled: !leftArrowVisible }}
        pointerEvents={leftArrowVisible ? "auto" : "none"}
        testID="anchor-garment-selector-left-button-container"
        style={[
          style.arrowContainer,
          {
            left: 0,
            paddingRight: style.arrowContainerInnerPadding,
            paddingLeft: style.arrowContainerOuterPadding,
          },
          leftArrowAnimatedStyle,
        ]}
      >
        <LinearGradient
          colors={[style.arrowContainerBackgroundColor, style.arrowContainerBackgroundColor, "transparent"]}
          end={{ x: 1, y: 0 }}
          locations={[0, 0.7, 1]}
          start={{ x: 0, y: 0 }}
          style={style.arrowGradient}
        />
        <ButtonIcon
          name="arrow_left"
          style={{ button: style.arrowButton, icon: style.arrowIcon }}
          testID="anchor-garment-selector-left-button"
          onPress={handleOnLeftArrowPress}
        />
      </Animated.View>

      <FlatList
        ref={flatListRef}
        contentContainerStyle={style.contentContainer}
        data={productVariants}
        keyExtractor={(item) => item.id}
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        testID="anchor-garment-selector-list"
        /**
         * This is needed to avoid the weird initial scroll behaviour (due to FlatList's internal measurements).
         */
        getItemLayout={(_, index) => ({
          length: style.itemWidth,
          offset: style.itemWidth * index,
          index,
        })}
        renderItem={({ item, index }) => {
          const isCurrentSelected = item.id === selectedAnchorProductVariantId;
          const lastItem = index === productVariants.length - 1;

          return (
            <View
              key={item.id}
              testID={`anchor-garment-selector-item-${item.id}`}
              style={[
                style.item,
                isDesktop ? style.itemDesktop : undefined,
                isCurrentSelected ? style.itemActive : undefined,
                lastItem ? style.itemLast : undefined,
              ]}
            >
              <TouchableWithoutFeedback testID={`item-${item.id}`} onPress={() => onPress(item.id)}>
                <View>
                  <Image
                    resizeMode="cover"
                    src={cdnImageUrl({ imageUrl: item.imageUrl, width: style.imageCdnWidth })}
                    style={style.image}
                  />
                </View>
              </TouchableWithoutFeedback>
            </View>
          );
        }}
        horizontal
        onContentSizeChange={handleOnContentSizeChange}
        onScroll={handleOnScroll}
        onScrollToIndexFailed={(info) => {
          const wait = new Promise((resolve) => setTimeout(resolve, 500));
          wait.then(() => {
            flatListRef.current?.scrollToIndex({ index: info.index, animated: true });
          });
        }}
      />

      <Animated.View
        accessibilityState={{ disabled: !rightArrowVisible }}
        pointerEvents={rightArrowVisible ? "auto" : "none"}
        testID="anchor-garment-selector-right-button-container"
        style={[
          style.arrowContainer,
          {
            right: 0,
            paddingLeft: style.arrowContainerInnerPadding,
            paddingRight: style.arrowContainerOuterPadding,
          },
          rightArrowAnimatedStyle,
        ]}
      >
        <LinearGradient
          colors={["transparent", style.arrowContainerBackgroundColor, style.arrowContainerBackgroundColor]}
          end={{ x: 1, y: 0 }}
          locations={[0, 0.3, 1]}
          start={{ x: 0, y: 0 }}
          style={style.arrowGradient}
        />
        <ButtonIcon
          name="arrow_right"
          style={{ button: style.arrowButton, icon: style.arrowIcon }}
          testID="anchor-garment-selector-right-button"
          onPress={handleOnRightArrowPress}
        />
      </Animated.View>
    </View>
  );
};

export type { AnchorGarmentSelectorProps };
export { AnchorGarmentSelector };
